/* ---------------------------------------------------------------------------
 * Cart gift tiers
 *
 * Every cart mutation made by the gift system goes through ONE serialized
 * queue (window.__cgtQueue). Nothing here ever runs two cart writes in
 * parallel, because Shopify's /cart/*.js endpoints are not transactional
 * against each other: concurrent writes lose updates and return section HTML
 * rendered from a cart state that no longer exists.
 *
 * All add/remove DECISIONS are made from live /cart.js state, never from the
 * server-rendered data-* attributes. Those attributes are a snapshot from
 * whenever the drawer HTML was last rendered and go stale the moment any other
 * code path (PDP add-to-cart, quick buy, qty stepper) refreshes the drawer.
 * ------------------------------------------------------------------------- */

// Set window.__cgtDebug = true in DevTools to get the verbose logs.
if (!window.__cgtLog) {
  window.__cgtLog = function () {
    if (!window.__cgtDebug) return;
    console.log.apply(console, ['[cart-gift-tiers]'].concat([].slice.call(arguments)));
  };
}

if (!window.__cgtCartRoot) {
  window.__cgtCartRoot = function () {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  };
}

// Authoritative cart state. Always bypasses the HTTP cache.
if (!window.__cgtFetchCart) {
  window.__cgtFetchCart = function () {
    return fetch(`${window.__cgtCartRoot()}cart.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    }).then((r) => {
      if (!r.ok) throw new Error(`cart.js ${r.status}`);
      return r.json();
    });
  };
}

if (!window.__cgtIsGiftLine) {
  window.__cgtIsGiftLine = function (item) {
    return !!(item && item.properties && item.properties._is_gift === 'true');
  };
}

/* The drawer HTML is rebuilt on every cart change, so anything that has to be
 * remembered between two paints (how many tickets were unlocked last time) is
 * kept on window. */
if (!window.__cgtShown) window.__cgtShown = {};

/* ---------------------------------------------------------------------------
 * Serialized mutation queue.
 * Every gift-driven cart write chains onto the previous one, so the guard and
 * the tier sync can never interleave with each other or with themselves.
 * ------------------------------------------------------------------------- */
if (!window.__cgtEnqueue) {
  window.__cgtQueue = Promise.resolve();
  window.__cgtEnqueue = function (task) {
    const run = window.__cgtQueue.then(
      () => task(),
      () => task() // a failed predecessor must not poison the queue
    );
    // Keep the chain alive regardless of this task's outcome.
    window.__cgtQueue = run.catch(() => {});
    return run;
  };
}

// Variants that Shopify refused to add (sold out, unavailable, …). Retrying
// them on every cart interaction would spam failed requests forever.
if (!window.__cgtUnaddableVariants) window.__cgtUnaddableVariants = new Set();

/* ---------------------------------------------------------------------------
 * Shared cart-section refresh + checkout guard
 * ------------------------------------------------------------------------- */
if (!window.enforceGiftQtyLimit) {
  window.__cartOpsInFlight = window.__cartOpsInFlight || 0;

  // body class + !important survives GoKwik's MutationObserver, which
  // re-enables checkout buttons after every DOM change.
  if (!document.getElementById('gift-guard-styles')) {
    const s = document.createElement('style');
    s.id = 'gift-guard-styles';
    s.textContent =
      'body.cart-op-in-flight .gokwik-checkout button,' +
      'body.cart-op-in-flight #gokwik-buy-now,' +
      'body.cart-op-in-flight [name="checkout"],' +
      'body.cart-op-in-flight .cart__checkout-button,' +
      'body.cart-op-in-flight .btn--checkout{' +
      'pointer-events:none!important;opacity:0.5!important;cursor:not-allowed!important}';
    document.head.appendChild(s);
  }

  window._disableCheckout = function () {
    window.__cartOpsInFlight++;
    document.body.classList.add('cart-op-in-flight');
  };

  window._enableCheckout = function () {
    window.__cartOpsInFlight = Math.max(0, window.__cartOpsInFlight - 1);
    if (window.__cartOpsInFlight === 0) {
      document.body.classList.remove('cart-op-in-flight');
    }
  };

  // Paints returned section HTML into the drawer. Accepts either a full
  // document string or the sections payload from a cart write.
  window.__cgtPaintSection = function (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const formHtml = doc.getElementById('AjaxCartForm');
    const subtotalHtml = doc.getElementById('AjaxCartSubtotal');
    const cartEl = document.getElementById('AjaxCartForm');

    if (cartEl && formHtml) {
      cartEl.innerHTML = formHtml.innerHTML;
      if (typeof cartEl.ajaxifyCartItems === 'function') cartEl.ajaxifyCartItems();
    }
    const subtotal = document.getElementById('AjaxCartSubtotal');
    if (subtotal && subtotalHtml) subtotal.innerHTML = subtotalHtml.innerHTML;

    document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
      const src = cartEl && cartEl.querySelector('[data-cart-count]');
      if (src) elm.textContent = src.textContent;
    });
    document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
      const src = cartEl && cartEl.querySelector('[data-cart-total]');
      if (src) elm.textContent = src.textContent;
    });
  };

  /* Busy overlay. Ref-counted so nested callers (qty stepper -> tier sync)
     can each show/hide without the overlay flickering in between. */
  window.__cgtBusyCount = window.__cgtBusyCount || 0;

  window.__cgtBusyHost = function () {
    // Anchor to .sidebar__body, not the drawer itself: the drawer also contains
    // the recommendations column (and .sidebar__body is only 65% wide on
    // desktop), so covering the whole drawer put the spinner off-centre.
    // .sidebar__body already carries position:relative in component-sidebar.css.
    const drawer = document.getElementById('site-cart-sidebar');
    if (drawer) return drawer.querySelector('.sidebar__body') || drawer;
    return document.getElementById('AjaxCartForm');
  };

  window.__cgtBusy = function (show) {
    if (show) {
      window.__cgtBusyCount++;
      const host = window.__cgtBusyHost();
      if (!host || host.querySelector('[data-cgt-loader]')) return;
      const loader = document.createElement('div');
      loader.className = 'cart-gift-tiers__loader';
      loader.setAttribute('data-cgt-loader', '');
      loader.setAttribute('role', 'status');
      loader.setAttribute('aria-live', 'polite');
      loader.setAttribute('aria-label', 'Updating cart');
      host.appendChild(loader);
      return;
    }
    window.__cgtBusyCount = Math.max(0, window.__cgtBusyCount - 1);
    if (window.__cgtBusyCount === 0) {
      document.querySelectorAll('[data-cgt-loader]').forEach((el) => el.remove());
    }
  };

  window.__cgtRefreshCartHTML = function () {
    if (!document.getElementById('AjaxCartForm')) return Promise.resolve();
    return fetch('?section_id=helper-cart')
      .then((r) => r.text())
      .then((text) => window.__cgtPaintSection(text))
      .catch((err) => window.__cgtLog('section refresh failed', err));
  };

  // Every gift variant the theme knows about (tier gifts + legacy default gift).
  window.__cgtGiftVariantIds = function () {
    const ids = new Set();
    document.querySelectorAll('cart-gift-tiers').forEach((el) => {
      try {
        JSON.parse(el.getAttribute('data-tiers') || '[]').forEach((t) => {
          if (t.variant_id) ids.add(Number(t.variant_id));
          (t.extra_variant_ids || []).forEach((id) => {
            if (id) ids.add(Number(id));
          });
        });
      } catch (e) {}
    });
    return ids;
  };

  /* Gift integrity guard, serialized through the queue:
   *   1. gift line (_is_gift) with qty > 1  -> clamp to 1
   *   2. known gift variant without the property -> remove (manual/console add)
   */
  window.enforceGiftQtyLimit = function () {
    return window.__cgtEnqueue(function () {
      window._disableCheckout();

      return window.__cgtFetchCart()
        .then((cart) => {
          const giftVariantIds = window.__cgtGiftVariantIds();
          const updates = {};

          (cart.items || []).forEach((item) => {
            const isGiftProp = window.__cgtIsGiftLine(item);
            const isGiftVariant = giftVariantIds.has(Number(item.variant_id));

            if (isGiftProp && item.quantity > 1) {
              updates[item.key] = 1;
            } else if (isGiftVariant && !isGiftProp) {
              updates[item.key] = 0;
            }
          });

          if (!Object.keys(updates).length) return null;

          window.__cgtLog('guard correcting cart', updates);
          window.__cgtInternalWrite = true;
          return fetch(`${window.__cgtCartRoot()}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates, sections: 'helper-cart' }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`update.js ${r.status}`))))
            .then((res) => {
              if (res && res.sections && res.sections['helper-cart']) {
                window.__cgtPaintSection(res.sections['helper-cart']);
              } else {
                return window.__cgtRefreshCartHTML();
              }
            })
            .finally(() => {
              window.__cgtInternalWrite = false;
            });
        })
        .catch((err) => window.__cgtLog('guard failed', err))
        .finally(() => window._enableCheckout());
    });
  };

  // Trigger 1: page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.enforceGiftQtyLimit());
  } else {
    window.enforceGiftQtyLimit();
  }

  // Trigger 2: tab regains focus — catches cross-tab / DevTools manipulation
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') window.enforceGiftQtyLimit();
  });

  // Trigger 3: fetch interceptor — catches /cart/add.js calls the theme did not
  // make (extensions, console scripts, third-party apps). Theme-owned writes
  // set __cartGiftTiersSyncing / __cgtInternalWrite so they are ignored.
  (function () {
    const _origFetch = window.fetch;
    window.fetch = function (input) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isExternalAdd =
        /\/cart\/(add|change|update)\.js/.test(url) &&
        !window.__cartGiftTiersSyncing &&
        !window.__cgtInternalWrite;
      const promise = _origFetch.apply(this, arguments);
      if (isExternalAdd) {
        promise
          .then(() => {
            clearTimeout(window.__giftGuardTimer);
            window.__giftGuardTimer = setTimeout(() => {
              // Re-sync tiers too: an external add changes the item count.
              const el = document.querySelector('cart-gift-tiers');
              if (el && typeof el.syncGifts === 'function') el.syncGifts();
              else window.enforceGiftQtyLimit();
            }, 400);
          })
          .catch(() => {});
      }
      return promise;
    };
  })();
}

if (typeof CartGiftTiers !== 'function') {
  const MAX_SYNC_PASSES = 3;
  // Cart attribute that remembers which excluded product, if any, came first in an
  // empty cart and so counts as 1 item (see _resolveItems). cart-gift-credit.liquid
  // reads the same attribute, so the tickets and the gifts agree.
  const FIRST_EXCLUDED_ATTR = '_gift_first_excluded';

  // At most this many mutating syncs per window before we stand down.
  const SYNC_BUDGET = 6;
  const SYNC_BUDGET_WINDOW_MS = 10000;

  window.__cgtSyncBudget = window.__cgtSyncBudget || { count: 0, since: 0 };

  function claimSyncBudget() {
    const now = Date.now();
    const b = window.__cgtSyncBudget;
    if (now - b.since > SYNC_BUDGET_WINDOW_MS) {
      b.since = now;
      b.count = 0;
    }
    b.count += 1;
    if (b.count > SYNC_BUDGET) {
      console.warn('[cart-gift-tiers] sync budget exhausted; standing down briefly');
      return false;
    }
    return true;
  }

  class CartGiftTiers extends HTMLElement {
    constructor() {
      super();
      // Headless (cart page): data + sync only, no tickets, no overlay.
      this.headless = this.getAttribute('data-headless') === 'true';
      this.tiers = this._parseJSON('data-tiers', []);
      // Gift wrap is a paid add-on, not one of the "items" that unlock gifts.
      this.giftWrapProductId = Number(this.getAttribute('data-gift-wrap-product-id')) || 0;
      // Bundle products count as more than one item: { productId: items per unit }.
      this.bundleWeights = this._parseJSON('data-bundle-weights', {});

      if (!this.tiers.length) return;

      if (!this.headless && !window.__cgtWarnedNoGift) {
        const missing = this.tiers.map((t, i) => (t.variant_id ? 0 : i + 1)).filter(Boolean);
        if (missing.length) {
          window.__cgtWarnedNoGift = true;
          console.warn(
            `[cart-gift-tiers] tier ${missing.join(', ')} has no gift product, so no gift will be added for it. ` +
              'Pick one in Theme settings > Cart > Cart Gift Tiers.'
          );
        }
      }

      // The tickets and banner arrive fully rendered from the server; all that
      // is left to do here is animate any ticket that was unlocked by this
      // change, and reconcile the actual gift lines against live cart state.
      this._animateNewlyUnlocked();
      this.syncGifts();
    }

    _parseJSON(attr, fallback) {
      try {
        return JSON.parse(this.getAttribute(attr) || JSON.stringify(fallback));
      } catch (e) {
        return fallback;
      }
    }

    // A ticket that is unlocked now but was not at the previous paint cross-fades
    // from locked to filled and its circle pops. The new element is rendered
    // already unlocked, so the card is dropped back to locked for a moment first.
    _animateNewlyUnlocked() {
      if (this.headless) return;
      const cards = Array.from(this.querySelectorAll('[data-cgt-card]'));
      const unlocked = cards.filter((card) => card.classList.contains('is-unlocked')).length;
      const previous = window.__cgtShown.unlocked;
      window.__cgtShown.unlocked = unlocked;

      if (
        previous === undefined ||
        unlocked <= previous ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }

      const fresh = cards.filter((card, i) => i >= previous && card.classList.contains('is-unlocked'));
      fresh.forEach((card) => {
        card.classList.remove('is-unlocked');
        card.classList.add('is-just-unlocked');
      });
      this.getBoundingClientRect(); // commit the locked look before switching
      setTimeout(() => {
        fresh.forEach((card) => card.classList.add('is-unlocked'));
        setTimeout(() => fresh.forEach((card) => card.classList.remove('is-just-unlocked')), 900);
      }, 50);
    }

    /* Items for the gifts = units of everything except the auto-added gift lines,
     * the gift-wrap product and the excluded products. A bundle product (Theme
     * settings > Cart Gift Tiers) counts as 2 or 3 items per unit: it holds that
     * many products. Gift lines are ignored on purpose: counting them would let a
     * gift unlock the next gift (which would cascade), and removing a gift could
     * drop the cart below its own tier (which would add/remove forever).
     * The Shopify discounts (10% at 2, 20% at 3 items) do count them, by design.
     *
     * One exception: an excluded product that is the first product put in an empty
     * cart counts as 1 item (whatever its quantity), so it earns gift 1. Added
     * after another product it counts 0, and so does every other excluded product.
     * The decision is kept in the cart attribute FIRST_EXCLUDED_ATTR:
     *   ''        not decided: an excluded product with no other product in the
     *             cart is the first one and gets the credit; with another product
     *             in the cart nobody gets it
     *   'none'    nobody gets the credit until the cart is empty again
     *   <id>      that product gets the credit while it stays in the cart; once it
     *             is removed the state becomes 'none'
     * An empty cart (gift lines do not count) starts over.
     * Returns the item count and the state the attribute should have now. */
    _resolveItems(cart) {
      const raw = String((cart.attributes || {})[FIRST_EXCLUDED_ATTR] || '');
      const lines = (cart.items || []).filter((item) => !window.__cgtIsGiftLine(item));
      if (!lines.length) return { itemCount: 0, state: '', raw };

      let others = 0;
      const excluded = [];
      lines.forEach((item) => {
        if (this.giftWrapProductId && Number(item.product_id) === this.giftWrapProductId) return;
        // 0 = excluded product, 2 or 3 = bundle product, no entry = 1.
        const weight = Number(this.bundleWeights[item.product_id]);
        if (weight === 0) {
          excluded.push(Number(item.product_id));
          return;
        }
        others += item.quantity * (Number.isNaN(weight) ? 1 : weight);
      });

      let credit = 0;
      let state = raw;
      if (raw === 'none') {
        credit = 0;
      } else if (raw !== '') {
        if (excluded.indexOf(Number(raw)) !== -1) {
          credit = Number(raw);
        } else {
          state = 'none'; // the product that had the credit left the cart
        }
      } else if (excluded.length) {
        if (others === 0) {
          credit = excluded[0];
          state = String(credit);
        } else {
          state = 'none'; // an excluded product added after another product
        }
      }
      return { itemCount: others + (credit ? 1 : 0), state, raw };
    }

    /* Decide the mutations needed to bring `cart` in line with the tiers.
     * Pure function of live cart state — no reliance on rendered attributes.
     *
     * Works from the set of gift variants that SHOULD be in the cart right now,
     * so the rules hold however the tiers are configured:
     *   - exactly one line, quantity 1, per wanted gift variant
     *   - duplicate lines and quantities above 1 are cut back
     *   - a gift line for a variant that is not wanted (tier not reached, product
     *     changed in settings, leftover from the old default gift) is removed
     *   - a gift variant sitting in the cart WITHOUT the _is_gift property was not
     *     put there by the theme (manual add, console, extension): removed, and
     *     re-added properly if the tier qualifies. */
    _plan(cart) {
      const updates = {};
      const toAdd = [];
      const { itemCount, state, raw } = this._resolveItems(cart);

      // 1) Gift variants wanted right now. Gift N is for the Nth item:
      //    1 item -> gift 1, 2 items -> gifts 1+2, ...
      //    A tier can hold extra gifts (tier 3's second product), which come
      //    and go together with its main gift.
      const wanted = new Set();
      this.tiers.forEach((tier, index) => {
        const tierVariantIds = [tier.variant_id].concat(tier.extra_variant_ids || []).filter(Boolean);
        // Tier without a gift product configured — nothing to sync.
        if (!tierVariantIds.length) return;
        const reached = itemCount >= index + 1;
        if (reached) tierVariantIds.forEach((id) => wanted.add(Number(id)));
      });

      // 2) Every gift line: keep one qty-1 line per wanted variant, drop the rest.
      const kept = new Set();
      (cart.items || []).filter(window.__cgtIsGiftLine).forEach((line) => {
        const variantId = Number(line.variant_id);
        if (!wanted.has(variantId) || kept.has(variantId)) {
          updates[line.key] = 0;
          return;
        }
        kept.add(variantId);
        if (line.quantity !== 1) updates[line.key] = 1;
      });

      // 3) Wanted but missing: add it (unless Shopify already refused it once).
      wanted.forEach((variantId) => {
        if (!kept.has(variantId) && !window.__cgtUnaddableVariants.has(variantId)) toAdd.push(variantId);
      });

      // 4) Anti-tamper.
      const giftVariantIds = window.__cgtGiftVariantIds();
      (cart.items || []).forEach((item) => {
        if (window.__cgtIsGiftLine(item)) return;
        if (giftVariantIds.has(Number(item.variant_id))) updates[item.key] = 0;
      });

      // 5) Keep the first-excluded decision in the cart (null when it is up to date).
      const attributes = state !== raw ? { [FIRST_EXCLUDED_ATTR]: state } : null;

      return { updates, toAdd, itemCount, attributes };
    }

    _write(path, payload) {
      window.__cgtInternalWrite = true;
      // Ask for the drawer HTML in the same request. The response is rendered
      // after this write lands, so when the loop converges right afterwards we
      // can paint it directly instead of spending another round trip.
      return fetch(`${window.__cgtCartRoot()}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: 'helper-cart' }),
      })
        .then((r) => r.json().then((body) => ({ ok: r.ok, status: r.status, body })))
        .finally(() => {
          window.__cgtInternalWrite = false;
        });
    }

    /* Reconcile gift lines with the tiers.
     *
     * seedCart  — a cart object the caller already has (e.g. the /cart/change.js
     *             response). Saves the first round trip and, more importantly,
     *             lets the caller hand us fresher state than a re-read would.
     * opts.forcePaint — repaint the drawer even when nothing changed, so a
     *             caller can delegate its own repaint to us and we end up
     *             painting exactly once.
     *
     * Runs on the shared queue, sequentially (update THEN add — never both in
     * flight), re-reading the cart between passes so the next decision sees the
     * result of the previous write. Loops until a pass finds nothing to do,
     * which is what makes a tier crossed mid-flight get picked up instead of
     * being lost until the customer happens to touch the cart again.
     *
     * Nothing is painted until the cart has settled. Painting mid-sequence is
     * what used to flash a cart still holding a gift it no longer qualified for.
     */
    syncGifts(seedCart, opts) {
      const forcePaint = !!(opts && opts.forcePaint);
      if (!this.tiers.length) {
        return forcePaint ? window.__cgtRefreshCartHTML() : Promise.resolve();
      }

      return window.__cgtEnqueue(() => {
        window.__cartGiftTiersSyncing = true;
        window._disableCheckout();

        let mutated = false;
        let addedSomething = false;
        let converged = false;
        let pendingHTML = null;
        let busy = false;

        const sectionOf = (res) =>
          (res && res.ok && res.body && res.body.sections && res.body.sections['helper-cart']) || null;

        const pass = (n, seed) => {
          if (n >= MAX_SYNC_PASSES) {
            window.__cgtLog('sync hit pass limit; stopping');
            return Promise.resolve();
          }

          const cartPromise = seed ? Promise.resolve(seed) : window.__cgtFetchCart();

          return cartPromise.then((cart) => {
            const { updates, toAdd, itemCount, attributes } = this._plan(cart);
            const hasUpdates = Object.keys(updates).length > 0;

            window.__cgtLog(`pass ${n}`, { itemCount, updates, toAdd, attributes });

            if (!hasUpdates && !toAdd.length && !attributes) {
              converged = true;
              return Promise.resolve();
            }

            if (!claimSyncBudget()) return Promise.resolve();

            mutated = true;
            if (!busy) {
              busy = true;
              window.__cgtBusy(true);
            }

            // Seed for the next pass. update.js returns the whole cart, so it
            // can be reused directly; add.js returns only the added lines, so
            // that path has to re-read.
            let nextSeed = null;

            // Removals first: they free the cart up before anything is added,
            // and they must not race the add.
            const updateBody = {};
            if (hasUpdates) updateBody.updates = updates;
            if (attributes) updateBody.attributes = attributes;
            const step =
              hasUpdates || attributes
                ? this._write('cart/update.js', updateBody)
                : Promise.resolve(null);

            return step
              .then((res) => {
                if (res && !res.ok) {
                  window.__cgtLog('update.js rejected', res.status, res.body);
                } else if (res) {
                  nextSeed = res.body && res.body.items ? res.body : null;
                  pendingHTML = sectionOf(res) || pendingHTML;
                }
                if (!toAdd.length) return null;

                const addBody = (ids) => ({
                  items: ids.map((id) => ({ id, quantity: 1, properties: { _is_gift: 'true' } })),
                });
                return this._write('cart/add.js', addBody(toAdd)).then((first) => {
                  if (first.ok || toAdd.length < 2) return { res: first, failed: first.ok ? [] : toAdd };
                  // Shopify rejects the whole batch when one gift is unavailable
                  // (e.g. not on the Online Store channel). Retry one by one so
                  // only the broken gift is given up on, not the valid ones.
                  const failed = [];
                  let last = first;
                  return toAdd
                    .reduce(
                      (chain, id) =>
                        chain.then(() =>
                          this._write('cart/add.js', addBody([id])).then((r) => {
                            if (r.ok) last = r;
                            else {
                              failed.push(id);
                              console.warn(
                                '[cart-gift-tiers] gift add rejected by Shopify',
                                r.status,
                                r.body && (r.body.description || r.body.message),
                                'variant',
                                id
                              );
                            }
                          })
                        ),
                      Promise.resolve()
                    )
                    .then(() => ({ res: failed.length < toAdd.length ? last : first, failed }));
                });
              })
              .then((out) => {
                if (out) {
                  const { res, failed } = out;
                  if (res.ok) {
                    addedSomething = true;
                    pendingHTML = sectionOf(res) || pendingHTML;
                    nextSeed = null; // add.js returns line items, not the cart
                  }
                  // 422 = sold out / unavailable / limit reached. Blacklist so we
                  // stop retrying on every cart interaction.
                  if (toAdd.length === 1 && failed.length) {
                    console.warn(
                      '[cart-gift-tiers] gift add rejected by Shopify',
                      res.status,
                      res.body && (res.body.description || res.body.message)
                    );
                  }
                  failed.forEach((id) => window.__cgtUnaddableVariants.add(Number(id)));
                }
                return pass(n + 1, nextSeed);
              });
          });
        };

        return pass(0, seedCart)
          .then(() => {
            if (addedSomething) this._fireConfetti();

            // Only trust the piggy-backed HTML when the loop actually settled;
            // otherwise it may predate a write we never verified.
            if (mutated && converged && pendingHTML) {
              return window.__cgtPaintSection(pendingHTML);
            }
            if (mutated || forcePaint) return window.__cgtRefreshCartHTML();
            return null;
          })
          .catch((err) => console.warn('[cart-gift-tiers] sync failed', err))
          .finally(() => {
            window.__cartGiftTiersSyncing = false;
            if (busy) window.__cgtBusy(false);
            window._enableCheckout();
          });
      });
    }

    _fireConfetti() {
      const canvas = document.getElementById('cart-gift-tiers-confetti');
      if (!canvas || typeof confetti !== 'function') return;
      try {
        canvas.confetti =
          canvas.confetti || confetti.create(canvas, { resize: true, useWorker: true });
        canvas.confetti({ spread: 70, origin: { y: 0.5 } });
      } catch (e) {
        // swallow — confetti is a nice-to-have, never break the cart flow
      }
    }
  }

  if (typeof customElements.get('cart-gift-tiers') === 'undefined') {
    customElements.define('cart-gift-tiers', CartGiftTiers);
  }
}
